import React from 'react';
import {
  Box,
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import { MileageDeclarationTableRow } from '../types';

const TABLE_HEADERS = ['Vehicle Number', 'Start Mileage', 'End Mileage', 'Total Bus Mileage'] as const;

const useStyles = makeStyles(() => ({
  tableActions: {
    textAlign: 'center',
    marginTop: 16,
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
  },
  submitButton: {
    marginTop: 16,
  },
  actionButton: {
    marginLeft: 8,
  },
}));

interface MileageDataTableProps {
  data: MileageDeclarationTableRow[]; // full dataset
  initialRows?: number;
}

export const MileageDataTable: React.FC<MileageDataTableProps> = ({ data, initialRows = 25 }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = React.useState<number>(Math.min(initialRows, data.length));

  // Filter out the "Example" row
  const filteredData = React.useMemo(() => {
    return data.filter((row) => {
      const vehicle = String(row['Vehicle Number'] ?? '').toLowerCase();
      return !vehicle.includes('example');
    });
  }, [data]);

  React.useEffect(() => {
    setVisibleCount(Math.min(initialRows, filteredData.length));
  }, [filteredData, initialRows]);

  return (
    <>
      <TableContainer>
        <MuiTable>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(0, visibleCount).map((row, idx) => (
              <TableRow key={idx}>
                {TABLE_HEADERS.map((header) => (
                  <TableCell key={header}>{row[header as keyof MileageDeclarationTableRow] ?? ''}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {visibleCount < filteredData.length && (
        <Box className={classes.tableActions}>
          <Button
            variant="outlined"
            onClick={() => setVisibleCount(Math.min(visibleCount + initialRows, filteredData.length))}
          >
            {t('Show More')} ({visibleCount} / {filteredData.length})
          </Button>
          <Button
            variant="outlined"
            onClick={() => setVisibleCount(filteredData.length)}
            className={classes.actionButton}
          >
            {t('Show All')}
          </Button>
        </Box>
      )}
    </>
  );
};
